import React, { useState, useCallback } from 'react';


import { Stack } from 'ui/stack';

import Modal from 'components/partials/modal/modal';
import Button from 'components/partials/button/button';
import { Checkbox } from 'ui/checkbox';
import { Radio } from 'ui/radio';
import Input from 'components/partials/input/input';
import toast from 'components/partials/toast/toast';

import {
  useCreateRegime,
  useCreateRegimeGroup,
} from '../../../hooks/use-aid-regimes';

import {
  BASELINE_POPULATIONS,
  POPULATION_TYPES,
} from '../matrix-step.constants';

import type { PopulationType } from '../matrix-step.constants';

interface ExistingGroup {
  id: string | number;
  name: string;
}

const ALL_BASELINE_VALUES = BASELINE_POPULATIONS.map((p) => p.value);

const getSelectedPopulationsInCreationOrder = (selectedValues: string[]) =>
  BASELINE_POPULATIONS.filter((population) =>
    selectedValues.includes(population.value)
  ).sort((a, b) => {
    if (a.value === 'COMMUTER') return -1;
    if (b.value === 'COMMUTER') return 1;

    return a.label.localeCompare(b.label);
  });

interface AddRegimeModalProps {
  userId: string;
  modelId: string | number;
  existingGroups?: ExistingGroup[];
  onClose: () => void;
  onSuccess?: () => void;
}

const AddRegimeModal = ({
  userId,
  modelId,
  existingGroups = [],
  onClose,
  onSuccess,
}: AddRegimeModalProps): React.JSX.Element => {
  const [populationType, setPopulationType] = useState<PopulationType>(
    POPULATION_TYPES.BASELINE
  );
  const [selectedPopulations, setSelectedPopulations] =
    useState<string[]>(ALL_BASELINE_VALUES);
  const [pellSelected, setPellSelected] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createRegimeMutation = useCreateRegime();
  const createRegimeGroupMutation = useCreateRegimeGroup();

  const isBaseline = populationType === POPULATION_TYPES.BASELINE;
  const trimmedName = nameInput.trim();
  const hasExistingGroup = existingGroups.length > 0;

  const isFormValid = isBaseline
    ? !!selectedPopulations.length && (hasExistingGroup || !!trimmedName)
    : hasExistingGroup || !!trimmedName;

  const handlePopulationTypeChange = useCallback(
    (value: string | undefined): void => {
      if (!value) return;

      const newType = value as PopulationType;

      setPopulationType(newType);

      if (newType === POPULATION_TYPES.BASELINE) {
        setSelectedPopulations(ALL_BASELINE_VALUES);
      } else {
        setSelectedPopulations([]);
      }
    },
    []
  );

  const handlePopulationToggle = useCallback(
    (populationValue: string): void => {
      setSelectedPopulations((prev) =>
        prev.includes(populationValue)
          ? prev.filter((p) => p !== populationValue)
          : [...prev, populationValue]
      );
    },
    []
  );

  const handleAdd = useCallback(async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      let groupId: string | number;

      if (hasExistingGroup) {
        groupId = existingGroups[0].id;
      } else {
        const newGroup = (await createRegimeGroupMutation.mutateAsync({
          userId,
          modelId,
          data: { name: trimmedName, enabled: true },
        })) as { id: string | number };

        groupId = newGroup.id;
      }

      if (isBaseline) {
        const populationsToCreate =
          getSelectedPopulationsInCreationOrder(selectedPopulations);

        for (const population of populationsToCreate) {
          await createRegimeMutation.mutateAsync({
            userId,
            modelId,
            data: {
              name: population.label,
              groupId,
              population: population.value,
              pell: pellSelected,
            },
          });
        }

        toast.success({
          title:
            populationsToCreate.length === 1
              ? 'Regime created'
              : 'Regimes created',
          message:
            populationsToCreate.length === 1
              ? `"${populationsToCreate[0].label}" has been added successfully.`
              : `${populationsToCreate.length} regimes have been added successfully.`,
        });
      } else {
        await createRegimeMutation.mutateAsync({
          userId,
          modelId,
          data: {
            name: 'Custom',
            groupId,
            population: null,
            pell: pellSelected,
          },
        });

        toast.success({
          title: 'Regime created',
          message: '"Custom" has been added successfully.',
        });
      }

      onSuccess?.();
    } catch (error) {
      const err = error as { data?: { detail?: string } };

      toast.error({
        title: 'Error',
        message:
          err?.data?.detail || 'Failed to create regime. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isFormValid,
    isBaseline,
    trimmedName,
    hasExistingGroup,
    existingGroups,
    createRegimeGroupMutation,
    createRegimeMutation,
    userId,
    modelId,
    selectedPopulations,
    pellSelected,
    onSuccess,
  ]);

  const footer = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      <Button
        type="button"
        variant="brand-filled"
        size="md"
        disabled={!isFormValid}
        loading={isSubmitting}
        onClick={handleAdd}
        data-testid="add-regime-button"
      >
        Add
      </Button>
      <Button
        type="button"
        variant="reserve-text"
        size="md"
        onClick={onClose}
        data-testid="cancel-regime-button"
      >
        Cancel
      </Button>
    </Stack>
  );

  return (
    <Modal
      headerText="Add regime(s)"
      onCloseCallback={onClose}
      showCloseButton
      disableBackdropClick
      footer={footer}
      skipUnmountOnClose
      primaryAction={{
        onClick: handleAdd,
        disabled: !isFormValid,
        loading: isSubmitting,
      }}
      modalClassName="p-5"
      headerClassName="!text-2xl font-bold"
      data-testid="add-regime-modal"
    >
      <div className="mt-4 space-y-3">
        <Stack sx={{ gap: 1.5 }}>
          <Stack
            component="fieldset"
            sx={{ gap: 1.5, m: 0, p: 0, border: 0, minWidth: 0 }}
          >
            <legend className="sr-only">Population type</legend>
            <Stack sx={{ gap: 1 }}>
              <Radio
                value={POPULATION_TYPES.BASELINE}
                label="Based on baseline populations"
                id="modal-population-type-baseline"
                name="modal-population-type"
                checked={isBaseline}
                onChange={() =>
                  handlePopulationTypeChange(POPULATION_TYPES.BASELINE)
                }
              />
              <Stack direction="row" sx={{ gap: 1.5, pl: 3.5 }}>
                {BASELINE_POPULATIONS.map((population) => (
                  <Checkbox
                    key={population.value}
                    id={`population-${population.value}`}
                    label={population.label}
                    value={population.value}
                    checked={selectedPopulations.includes(population.value)}
                    onChange={() => handlePopulationToggle(population.value)}
                    disabled={!isBaseline}
                    data-testid={`checkbox-population-${population.value}`}
                  />
                ))}
              </Stack>
            </Stack>

            <Radio
              value={POPULATION_TYPES.CUSTOM}
              label="Custom populations only"
              id="modal-population-type-custom"
              name="modal-population-type"
              checked={populationType === POPULATION_TYPES.CUSTOM}
              onChange={() =>
                handlePopulationTypeChange(POPULATION_TYPES.CUSTOM)
              }
            />
          </Stack>

          <Checkbox
            id="modal-pell-recipients"
            label="Pell Recipients"
            value="pell"
            checked={pellSelected}
            onChange={() => setPellSelected((prev) => !prev)}
            data-testid="checkbox-modal-pell-recipients"
          />
        </Stack>

        <Input
          label="Group name"
          labelFor="group-name"
          labelInTop
          placeholder="Type group name..."
          value={hasExistingGroup ? existingGroups[0].name : nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          disabled={hasExistingGroup}
          inputCustomClass="h-10"
          customClass="text-md"
          data-testid="group-name-input"
        />
      </div>
    </Modal>
  );
};

export default AddRegimeModal;
